import { useState } from "react";
import { useTranslation } from "react-i18next";

interface Member {
  displayName: string;
  page: number;
  totalPages?: number | null;
  isViewer?: boolean;
}

interface Props {
  members: Member[];
  totalPages: number | null;
  addedAt?: Date | string | null;
  deadline?: Date | string | null;
  archivedAt?: Date | string | null;
  isAdmin?: boolean;
  onEditDeadline?: () => void;
  size?: "compact" | "default";
}

function getInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function toLocalDateOnly(d: Date | string): Date {
  const dt = typeof d === "string" ? new Date(d) : d;
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
}

function computeTimeFill(addedAt: Date | string, deadline: Date | string): number {
  const added = toLocalDateOnly(addedAt).getTime();
  const due = toLocalDateOnly(deadline).getTime();
  const today = toLocalDateOnly(new Date()).getTime();
  if (due <= added) return 100;
  return Math.min(100, Math.max(0, ((today - added) / (due - added)) * 100));
}

function computeOverdueDays(deadline: Date | string): number {
  const due = toLocalDateOnly(deadline).getTime();
  const today = toLocalDateOnly(new Date()).getTime();
  return Math.floor((today - due) / (1000 * 60 * 60 * 24));
}

function computeArchiveDiffDays(archivedAt: Date | string, deadline: Date | string): number {
  const archived = toLocalDateOnly(archivedAt).getTime();
  const due = toLocalDateOnly(deadline).getTime();
  return Math.round((archived - due) / (1000 * 60 * 60 * 24));
}

function formatDeadline(deadline: Date | string): string {
  const d = typeof deadline === "string" ? new Date(deadline) : deadline;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function ClubProgressBar({
  members,
  totalPages,
  addedAt,
  deadline,
  archivedAt,
  isAdmin = false,
  onEditDeadline,
  size = "default",
}: Props) {
  const [activeLabel, setActiveLabel] = useState<string | null>(null);
  const { t } = useTranslation();

  // Filter out members with 0 pages (SR-21-01: no dot for 0 pages)
  const activeMembersList = members.filter((m) => m.page >= 1);

  // Group members by their position on the track (SR-21-02: stacking)
  const canonicalPages = totalPages ?? 1;
  const grouped = new Map<number, Member[]>();
  for (const m of activeMembersList) {
    const pct = Math.min(100, Math.max(0, canonicalPages > 0 ? (m.page / canonicalPages) * 100 : 0));
    // Round to 2 decimal places to group members at the same page
    const rounded = Math.round(pct * 100) / 100;
    const key = rounded;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(m);
  }
  const positions = Array.from(grouped.entries());

  // Time-progress fill
  const hasDeadline = deadline != null && addedAt != null;
  const fillPct = hasDeadline ? computeTimeFill(addedAt!, deadline!) : 0;
  const overdueDays = hasDeadline ? computeOverdueDays(deadline!) : -1;
  const isOverdue = overdueDays >= 0;

  // Archive outcome label
  const archiveDiffDays =
    archivedAt != null && deadline != null
      ? computeArchiveDiffDays(archivedAt, deadline)
      : null;

  const dotSize = size === "compact" ? 20 : 26;
  const trackHeight = size === "compact" ? 10 : 14;

  function handleDotInteract(pct: number, names: string[], page: number) {
    void pct;
    const label = names.length === 1
      ? `${names[0]} · p.${page}`
      : `${names.join(", ")} · p.${page}`;
    setActiveLabel((prev) => (prev === label ? null : label));
  }

  return (
    <div className="w-full space-y-1" onClick={() => setActiveLabel(null)}>
      {/* Bar track — overflow visible so dots can protrude above/below the track */}
      <div
        className="relative w-full"
        style={{ height: `${trackHeight}px` }}
        role="presentation"
      >
        {/* Track background — separate div so we can clip it without clipping dots */}
        <div
          className="absolute inset-0 rounded-full overflow-hidden"
          style={{ background: "var(--color-muted)" }}
        >
          {/* Time-progress fill (background layer, shown only when deadline is set) */}
          {hasDeadline && (
            <div
              className="absolute left-0 top-0 h-full rounded-full transition-all duration-500"
              style={{
                width: `${fillPct}%`,
                background: isOverdue ? "var(--color-destructive)" : "var(--color-accent)",
                opacity: 0.3,
              }}
            />
          )}
        </div>

        {/* Member dots */}
        {positions.map(([pct, posMembers]) => {
          const page = posMembers[0].page;
          const names = posMembers.map((m) => m.displayName);
          const hasViewer = posMembers.some((m) => m.isViewer);
          const labelText = names.length === 1
            ? `${names[0]} · p.${page}`
            : `${names.join(", ")} · p.${page}`;
          const isActive = activeLabel === labelText;

          return (
            <div
              key={pct}
              className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 cursor-pointer"
              style={{ left: `${pct}%` }}
              onClick={(e) => {
                e.stopPropagation();
                handleDotInteract(pct, names, page);
              }}
              role="button"
              aria-label={labelText}
            >
              {/* Stack of dots (only outermost rendered as a circle with initials) */}
              <div
                className="rounded-full flex items-center justify-center font-medium text-white select-none"
                style={{
                  width: `${dotSize}px`,
                  height: `${dotSize}px`,
                  background: hasViewer ? "var(--color-accent)" : "var(--color-foreground)",
                  fontSize: size === "compact" ? "0.55rem" : "0.65rem",
                  boxShadow: posMembers.length > 1
                    ? "2px 2px 0 var(--color-muted), 4px 4px 0 oklch(60% 0.1 250 / 0.5)"
                    : "0 1px 3px rgba(0,0,0,0.2)",
                  outline: isActive ? "2px solid var(--color-accent)" : "none",
                  outlineOffset: "1px",
                }}
              >
                {posMembers.length > 1
                  ? `+${posMembers.length}`
                  : getInitials(posMembers[0].displayName)}
              </div>

              {/* Label tooltip — shown on click/tap, dismissed by clicking elsewhere */}
              {isActive && (
                <div
                  className="absolute bottom-full mb-1.5 pointer-events-none z-20 px-2 py-1 rounded-md text-xs whitespace-nowrap"
                  style={{
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "var(--color-foreground)",
                    color: "var(--color-card)",
                    boxShadow: "var(--shadow-md)",
                  }}
                >
                  {labelText}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Deadline and overdue label (below bar) */}
      {hasDeadline && (
        <div className="flex items-center gap-2 mt-1">
          {isOverdue ? (
            <p
              className="text-xs font-medium"
              style={{ color: "var(--color-destructive)" }}
            >
              {overdueDays === 0
                ? t("club.description.overdueZero")
                : t("club.description.overdue_other", { count: overdueDays })}
            </p>
          ) : (
            <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
              {t("club.description.deadlinePrefix")} {formatDeadline(deadline!)}
            </p>
          )}
          {isAdmin && onEditDeadline && (
            <button
              className="text-xs px-1.5 py-0.5 rounded transition-colors"
              style={{ color: "var(--color-muted-foreground)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-accent)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-muted-foreground)")}
              onClick={(e) => { e.stopPropagation(); onEditDeadline(); }}
              aria-label="Edit deadline"
            >
              ✎
            </button>
          )}
        </div>
      )}

      {/* Admin: show edit affordance even when no deadline is set */}
      {!hasDeadline && isAdmin && onEditDeadline && (
        <button
          className="text-xs mt-1 transition-colors"
          style={{ color: "var(--color-muted-foreground)" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-accent)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-muted-foreground)")}
          onClick={(e) => { e.stopPropagation(); onEditDeadline(); }}
          aria-label={t("club.description.deadlineLabel")}
        >
          + {t("club.description.deadlineLabel")}
        </button>
      )}

      {/* Archive outcome label (SR-21-05) */}
      {archiveDiffDays != null && (
        <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
          {archiveDiffDays < 0
            ? t("club.description.finishedEarly_other", { count: Math.abs(archiveDiffDays) })
            : archiveDiffDays > 0
            ? t("club.description.finishedLate_other", { count: archiveDiffDays })
            : t("club.description.finishedOnTime")}
        </p>
      )}
    </div>
  );
}
